import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsPage } from '../components/SettingsPage';
import { ServicesAside } from '../components/SettingsAsides';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { DurationSelect, EuroField, formatEuro, PageHeader, parseEuro, Select, SidePanel, Switch, TextArea, useFormatMinutes } from '../components/forms';
import { SortHandle, useSortable } from '../components/Sortable';
import { Button, Card, ErrorNote, Field } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';

type Duration =
  | { mode: 'simple'; minutes: number; cleanupMinutes: number }
  | { mode: 'processing'; beforeMinutes: number; processingMinutes: number; afterMinutes: number; cleanupMinutes: number };
type Variant = { id?: string; name: string; priceCents: number; duration: Duration; clientMinutes?: number };
type Service = {
  id: string;
  name: string;
  description: string | null;
  priceType: 'fixed' | 'from';
  priceCents: number;
  vatRate: '21' | '9' | '0';
  onlineBookable: boolean;
  duration: Duration;
  clientMinutes: number;
  staff: 'all' | string[];
  variants: Variant[];
};
type Category = { id: string; name: string; services: Service[] };
type TeamMember = { id: string; displayName: string; color: string };

export const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : e ? fallback : null);

function ServiceList({ category, onOpen, onAdd, onReorder }: { category: Category; onOpen: (s: Service) => void; onAdd: () => void; onReorder: (ids: string[]) => void }) {
  const t = useT();
  const fmt = useFormatMinutes();
  const sort = useSortable(category.services.map((x) => x.id), onReorder);
  return (
    <ul className="divide-y divide-line">
      {category.services.map((s) => (
        <li key={s.id} {...sort.itemProps(s.id)} className="flex items-center bg-surface pl-1.5">
          {category.services.length > 1 && <SortHandle label={s.name} onPointerDown={(e) => sort.start(e, s.id)} onKeyDown={(e) => sort.onKey(e, s.id)} />}
          <button className="flex flex-1 items-center gap-3 py-3 pl-2 pr-4 text-left hover:bg-subtle" onClick={() => onOpen(s)}>
            <span className="flex-1">
              <span className="font-medium">{s.name}</span>
              <span className="block text-sm text-muted">
                {s.variants.length === 1 ? t('services.variant1') : s.variants.length > 1 ? t('services.variants', { n: s.variants.length }) : fmt(s.clientMinutes)}
                {!s.onlineBookable && ` · ${t('services.notOnline')}`}
              </span>
            </span>
            <span className="tabular-nums">
              {s.priceType === 'from' && <span className="text-sm text-muted">{t('services.from')} </span>}
              {formatEuro(s.priceCents)}
            </span>
          </button>
        </li>
      ))}
      <li>
        <button className="w-full px-4 py-3 text-left text-sm font-semibold text-accent-strong hover:bg-subtle" onClick={onAdd}>
          + {t('services.newService')}
        </button>
      </li>
    </ul>
  );
}

export function ServicesPage() {
  const t = useT();
  const fmt = useFormatMinutes();
  const { orgId } = useParams({ from: '/s/$orgId/salon/diensten' });
  const qc = useQueryClient();
  const catalog = useQuery({ queryKey: ['catalog', orgId], queryFn: () => api<{ categories: Category[] }>(`/orgs/${orgId}/catalog`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['catalog', orgId] });
  const [editing, setEditing] = useState<{ service?: Service; categoryId: string } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const template = useMutation({ mutationFn: () => api(`/orgs/${orgId}/catalog/template`, { method: 'POST' }), onSuccess: refresh });
  const addCategory = useMutation({
    mutationFn: (name: string) => api(`/orgs/${orgId}/categories`, { method: 'POST', body: JSON.stringify({ name }) }),
    onSuccess: refresh,
  });
  const renameCategory = useMutation({
    mutationFn: (c: { id: string; name: string }) => api(`/orgs/${orgId}/categories/${c.id}`, { method: 'PATCH', body: JSON.stringify({ name: c.name }) }),
    onSuccess: refresh,
  });
  const deleteCategory = useMutation({ mutationFn: (id: string) => api(`/orgs/${orgId}/categories/${id}`, { method: 'DELETE' }), onSuccess: refresh });

  const [catPanel, setCatPanel] = useState<{ category?: Category } | null>(null);
  const [catName, setCatName] = useState('');
  function promptCategory(current?: Category) {
    setCatName(current?.name ?? '');
    setCatPanel({ category: current });
  }
  function saveCategory() {
    const name = catName.trim();
    if (!name) return;
    const done = { onSuccess: () => setCatPanel(null) };
    if (catPanel?.category) renameCategory.mutate({ id: catPanel.category.id, name }, done);
    else addCategory.mutate(name, done);
  }

  const categories = catalog.data?.categories ?? [];

  // Drag to reorder: the list moves right away, the server follows.
  const reorder = useMutation({
    mutationFn: (r: { kind: 'category' | 'service'; ids: string[]; categoryId?: string }) => api(`/orgs/${orgId}/catalog/order`, { method: 'PUT', body: JSON.stringify({ kind: r.kind, ids: r.ids }) }),
    onMutate: (r) => {
      qc.setQueryData<{ categories: Category[] }>(['catalog', orgId], (d) => {
        if (!d) return d;
        if (r.kind === 'category') return { categories: r.ids.map((id) => d.categories.find((c) => c.id === id)!).filter(Boolean) };
        return { categories: d.categories.map((c) => (c.id === r.categoryId ? { ...c, services: r.ids.map((id) => c.services.find((x) => x.id === id)!).filter(Boolean) } : c)) };
      });
    },
    onSettled: refresh,
  });
  const sortCats = useSortable(categories.map((c) => c.id), (ids) => reorder.mutate({ kind: 'category', ids }));

  return (
    <SettingsPage orgId={orgId} aside={<ServicesAside orgId={orgId} />}>
      <PageHeader
        orgId={orgId}
        title={t('services.title')}
        intro={t('services.intro')}
        action={
          categories.length > 0 && (
            <Button variant="secondary" onClick={() => promptCategory()}>
              + {t('services.newCategory')}
            </Button>
          )
        }
      />

      {catalog.isSuccess && categories.length === 0 && (
        <Card className="flex flex-col items-start gap-3 py-8">
          <h2 className="text-lg font-semibold">{t('services.empty.title')}</h2>
          <p className="max-w-prose text-muted">{t('services.empty.body')}</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => template.mutate()} disabled={template.isPending}>
              {t('services.useTemplate')}
            </Button>
            <Button variant="secondary" onClick={() => promptCategory()}>
              {t('services.startEmpty')}
            </Button>
          </div>
          <ErrorNote message={errorText(template.error, t('error.generic'))} />
        </Card>
      )}

      {categories.length > 0 && (
        <div className="flex flex-col gap-6">
          {categories.map((c) => (
            <section key={c.id} {...sortCats.itemProps(c.id)} className="overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface">
              <div className="flex items-center gap-1 border-b border-line bg-subtle py-1.5 pl-1.5 pr-4">
                {categories.length > 1 && <SortHandle label={c.name} onPointerDown={(e) => sortCats.start(e, c.id)} onKeyDown={(e) => sortCats.onKey(e, c.id)} />}
                <button
                  className="flex min-h-10 flex-1 items-center gap-2 pl-2 text-left font-semibold"
                  aria-expanded={!collapsed.has(c.id)}
                  onClick={() => setCollapsed((x) => (x.has(c.id) ? new Set([...x].filter((y) => y !== c.id)) : new Set([...x, c.id])))}
                >
                  <span className={`inline-block text-muted transition-transform ${!collapsed.has(c.id) ? 'rotate-90' : ''}`}>›</span>
                  {c.name}
                  <span className="text-sm font-normal text-muted">({c.services.length})</span>
                </button>
                <button className="rounded-[var(--radius-control)] px-2 py-1 text-sm text-muted hover:bg-surface" onClick={() => promptCategory(c)}>
                  {t('services.editCategory')}
                </button>
              </div>
              {!collapsed.has(c.id) && (
                <ServiceList
                  category={c}
                  onOpen={(svc) => setEditing({ service: svc, categoryId: c.id })}
                  onAdd={() => setEditing({ categoryId: c.id })}
                  onReorder={(ids) => reorder.mutate({ kind: 'service', ids, categoryId: c.id })}
                />
              )}
            </section>
          ))}
        </div>
      )}

      {catPanel && (
        <SidePanel
          open
          title={catPanel.category ? t('services.renameCategory') : t('services.newCategory')}
          onClose={() => setCatPanel(null)}
          footer={
            <>
              <Button onClick={saveCategory} disabled={addCategory.isPending || renameCategory.isPending}>
                {t('common.save')}
              </Button>
              <Button variant="ghost" onClick={() => setCatPanel(null)}>
                {t('common.cancel')}
              </Button>
              {catPanel.category && (
                <Button
                  variant="ghost"
                  className="ml-auto text-danger"
                  onClick={() => {
                    if (!window.confirm(t('services.deleteCategoryConfirm'))) return;
                    deleteCategory.mutate(catPanel.category!.id, { onSuccess: () => setCatPanel(null) });
                  }}
                >
                  {t('common.delete')}
                </Button>
              )}
            </>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveCategory();
            }}
          >
            <Field id="cat-name" required maxLength={80} label={t('services.categoryName')} value={catName} onChange={(e) => setCatName(e.target.value)} />
          </form>
        </SidePanel>
      )}

      {editing && (
        <ServicePanel
          orgId={orgId}
          categories={categories}
          service={editing.service}
          categoryId={editing.categoryId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}
    </SettingsPage>
  );
}

const simpleDefault: Duration = { mode: 'simple', minutes: 30, cleanupMinutes: 0 };
const totalOf = (d: Duration) => (d.mode === 'simple' ? d.minutes : d.beforeMinutes + d.processingMinutes + d.afterMinutes);

function ServicePanel({ orgId, categories, service, categoryId, onClose, onSaved }: {
  orgId: string;
  categories: Category[];
  service?: Service;
  categoryId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const team = useQuery({ queryKey: ['team', orgId], queryFn: () => api<{ team: TeamMember[] }>(`/orgs/${orgId}/team`) });
  const [name, setName] = useState(service?.name ?? '');
  const [cat, setCat] = useState(categoryId);
  const [price, setPrice] = useState(service ? (service.priceCents / 100).toFixed(2).replace('.', ',') : '');
  const [from, setFrom] = useState(service?.priceType === 'from');
  const [duration, setDuration] = useState<Duration>(service?.duration ?? simpleDefault);
  const [description, setDescription] = useState(service?.description ?? '');
  const [online, setOnline] = useState(service?.onlineBookable ?? true);
  const [vat, setVat] = useState(service?.vatRate);
  const [staff, setStaff] = useState<'all' | string[]>(service?.staff ?? 'all');
  const [variants, setVariants] = useState<Array<Variant & { priceText: string }>>(
    (service?.variants ?? []).map((v) => ({ ...v, priceText: (v.priceCents / 100).toFixed(2).replace('.', ',') })),
  );
  const hasAdvanced = !!service && (duration.mode === 'processing' || duration.cleanupMinutes > 0 || variants.length > 0 || staff !== 'all' || !online || !!description);
  const [more, setMore] = useState(hasAdvanced);
  const [localError, setLocalError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (payload: unknown) =>
      service
        ? api(`/orgs/${orgId}/services/${service.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : api(`/orgs/${orgId}/services`, { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: onSaved,
  });
  const remove = useMutation({ mutationFn: () => api(`/orgs/${orgId}/services/${service!.id}`, { method: 'DELETE' }), onSuccess: onSaved });

  function submit() {
    const cents = parseEuro(price);
    if (!name.trim() || cents === null) return setLocalError(t('service.price'));
    const vs = [];
    for (const v of variants) {
      const vc = parseEuro(v.priceText);
      if (!v.name.trim() || vc === null) return setLocalError(t('service.variantName'));
      vs.push({ id: v.id, name: v.name.trim(), priceCents: vc, duration: v.duration });
    }
    setLocalError(null);
    save.mutate({
      categoryId: cat,
      name: name.trim(),
      description: description.trim() || null,
      priceType: from ? 'from' : 'fixed',
      priceCents: cents,
      duration,
      ...(vat ? { vatRate: vat } : {}),
      onlineBookable: online,
      staff,
      variants: vs,
    });
  }

  const members = team.data?.team ?? [];
  const selected = staff === 'all' ? new Set(members.map((m) => m.id)) : new Set(staff);
  function toggleStaff(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setStaff(next.size === members.length ? 'all' : [...next]);
  }

  return (
    <SidePanel
      open
      title={service ? t('service.edit') : t('service.new')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={submit} disabled={save.isPending}>
            {t('common.save')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {service && (
            <Button variant="ghost" className="ml-auto text-danger" onClick={() => window.confirm(t('service.deleteConfirm')) && remove.mutate()}>
              {t('common.delete')}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field id="svc-name" required label={t('service.name')} value={name} onChange={(e) => setName(e.target.value)} />
        <Select id="svc-cat" label={t('service.category')} value={cat} onChange={(e) => setCat(e.target.value)}>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        {duration.mode === 'simple' && (
          <DurationSelect id="svc-duration" label={t('service.duration')} value={duration.minutes} onChange={(m) => setDuration({ ...duration, minutes: m })} />
        )}
        <EuroField id="svc-price" label={t('service.price')} hint={t('service.priceHint')} value={price} onChange={setPrice} />
        <Switch label={t('service.fromPrice')} hint={t('service.fromPriceHint')} checked={from} onChange={setFrom} />

        <button type="button" className="self-start text-sm font-semibold text-accent-strong hover:underline" aria-expanded={more} onClick={() => setMore(!more)}>
          {more ? t('common.fewerOptions') : t('common.moreOptions')}
        </button>

        {more && (
          <div className="flex flex-col gap-5 border-t border-line pt-4">
            <TextArea id="svc-desc" label={t('service.description')} maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />

            <div className="flex flex-col gap-3">
              <Switch
                label={t('service.processing')}
                hint={t('service.processingHint')}
                checked={duration.mode === 'processing'}
                onChange={(on) =>
                  setDuration(
                    on
                      ? { mode: 'processing', beforeMinutes: 20, processingMinutes: 30, afterMinutes: 20, cleanupMinutes: duration.cleanupMinutes }
                      : { mode: 'simple', minutes: totalOf(duration), cleanupMinutes: duration.cleanupMinutes },
                  )
                }
              />
              {duration.mode === 'processing' && (
                <div className="grid grid-cols-3 gap-2">
                  <DurationSelect id="svc-before" label={t('service.before')} value={duration.beforeMinutes} max={240} onChange={(m) => setDuration({ ...duration, beforeMinutes: m })} />
                  <DurationSelect id="svc-wait" label={t('service.wait')} value={duration.processingMinutes} max={240} onChange={(m) => setDuration({ ...duration, processingMinutes: m })} />
                  <DurationSelect id="svc-after" label={t('service.after')} value={duration.afterMinutes} max={240} onChange={(m) => setDuration({ ...duration, afterMinutes: m })} />
                </div>
              )}
            </div>

            <DurationSelect
              id="svc-cleanup"
              label={t('service.cleanup')}
              hint={t('service.cleanupHint')}
              value={duration.cleanupMinutes}
              allowZero
              max={60}
              onChange={(m) => setDuration({ ...duration, cleanupMinutes: m })}
            />

            <div className="flex flex-col gap-2">
              <p className="font-medium">{t('service.variants')}</p>
              <p className="text-sm text-muted">{t('service.variantsHint')}</p>
              {variants.map((v, i) => (
                <div key={v.id ?? i} className="grid grid-cols-[1fr_auto] gap-2 rounded-[var(--radius-control)] bg-subtle p-3">
                  <Field id={`var-name-${i}`} label={t('service.variantName')} value={v.name} onChange={(e) => setVariants(variants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                  <button type="button" className="self-end rounded-[var(--radius-control)] px-3 py-3 text-muted hover:bg-surface" aria-label={t('common.delete')} onClick={() => setVariants(variants.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                  <DurationSelect
                    id={`var-dur-${i}`}
                    label={t('service.duration')}
                    value={totalOf(v.duration)}
                    onChange={(m) => setVariants(variants.map((x, j) => (j === i ? { ...x, duration: { mode: 'simple', minutes: m, cleanupMinutes: duration.cleanupMinutes } } : x)))}
                  />
                  <EuroField id={`var-price-${i}`} label={t('service.price')} value={v.priceText} onChange={(p) => setVariants(variants.map((x, j) => (j === i ? { ...x, priceText: p } : x)))} />
                </div>
              ))}
              <button
                type="button"
                className="self-start text-sm font-semibold text-accent-strong hover:underline"
                onClick={() => {
                  const fresh = { name: '', priceText: price, priceCents: 0, duration: { mode: 'simple' as const, minutes: totalOf(duration), cleanupMinutes: duration.cleanupMinutes } };
                  // The first variant also keeps the original as "Standaard", so clients always choose between real options.
                  setVariants(variants.length === 0 ? [{ ...fresh, name: t('service.defaultVariant') }, fresh] : [...variants, fresh]);
                }}
              >
                + {t('service.addVariant')}
              </button>
            </div>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 font-medium">{t('service.who')}</legend>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={staff === 'all'} onChange={(e) => setStaff(e.target.checked ? 'all' : [])} />
                {t('service.everyone')}
              </label>
              {staff !== 'all' &&
                members.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 pl-6">
                    <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={selected.has(m.id)} onChange={() => toggleStaff(m.id)} />
                    <span className="size-3 rounded-full" style={{ background: m.color }} />
                    {m.displayName}
                  </label>
                ))}
            </fieldset>

            <Switch label={t('service.online')} hint={t('service.onlineHint')} checked={online} onChange={setOnline} />

            <Select id="svc-vat" label={t('service.vat')} hint={t('service.vatHint')} value={vat ?? ''} onChange={(e) => setVat((e.target.value || undefined) as Service['vatRate'] | undefined)}>
              {!service && <option value="">—</option>}
              <option value="9">9%</option>
              <option value="21">21%</option>
              <option value="0">0%</option>
            </Select>
          </div>
        )}
        <ErrorNote message={localError ?? errorText(save.error, t('error.generic'))} />
      </div>
    </SidePanel>
  );
}
